import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock3 } from "lucide-react";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Reveal } from "./Reveal";
import Seo from "./Seo";
import { BLOG_ARTICLES } from "@/data/blog";

export default function BlogPage() {
  return (
    <div className="min-h-screen bg-[#080C14]" data-testid="blog-page">
      <Seo
        title="Blog | Conseils mariage, DJ & ambiance — KLMProd"
        description="Conseils d'un DJ professionnel : budget mariage haut de gamme, questions à poser avant de réserver, éclairage LED de réception. Le blog KLMProd."
        path="/blog"
      />

      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="blog-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <Reveal>
          <p className="font-mono-brand text-xs uppercase tracking-[0.3em] text-cyan-400">Le blog KLMProd</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h1 className="font-display mt-5 max-w-3xl text-4xl font-extrabold tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            Conseils de DJ, <span className="text-gradient">sans langue de bois.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            Budget, organisation, lumière, musique : ce que nous expliquons à nos clients, écrit noir
            sur blanc.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_ARTICLES.map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.1} className="h-full">
              <Link
                to={`/blog/${a.slug}`}
                data-testid={`blog-card-${a.slug}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-cyan-500/15 bg-[#0E1524]/70 backdrop-blur-xl transition-transform duration-500 hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={a.img}
                    alt={a.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0E1524] via-[#0E1524]/30 to-transparent" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="flex items-center gap-3 text-[11px] text-slate-500">
                    <span className="flex items-center gap-1">
                      <CalendarDays size={11} className="text-cyan-400" />
                      {format(parseISO(a.date), "d MMMM yyyy", { locale: fr })}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock3 size={11} className="text-cyan-400" />
                      {a.minutes} min
                    </span>
                  </p>
                  <h2 className="font-display mt-3 text-lg font-bold leading-snug text-slate-100 transition-colors group-hover:text-cyan-300">
                    {a.title}
                  </h2>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-400">{a.excerpt}</p>
                  <span className="mt-4 flex items-center gap-1.5 text-xs font-bold text-cyan-300">
                    Lire l'article
                    <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
